import React from "react";
import Button from "components/button";
import { Download, FileText } from "lucide-react";
import Card from "components/card";

const DocumentInfo = ({ 
    document,
    hasAction,
    hasBorder,
}) => {
    if (!document) return null;

    const statusClass = document?.doc_status_name?.toLowerCase().replace(/\s+/g, "-");
    
    return (
        <Card className="grid w-full gap-4 border rounded-lg shadow-lg p-6">
            <div className="flex flex-col gap-2 text-left">
                <h2 className="label-gradient emerald text-[20px] font-semibold title-text">
                    {document?.doc_title}
                </h2>
                <p className="flex gap-1 text-sm font-medium muted-text">
                    <span>{document?.doc_type_name}</span>
                    <span>•</span>
                    <span>{document?.doc_date}</span>
                    <span>•</span>
                    <span>{document?.doc_size}</span>
                </p>

            </div>
            <div className="space-y-6 pt-4">
                <div className="flex flex-col gap-6 rounded-xl border border-slate-200">
                    <div
                    className={`
                        grid auto-rows-min items-start gap-1.5 px-6 pt-6
                        ${hasAction ? "grid-cols-[1fr_auto]" : "grid-cols-1"}
                        ${hasBorder ? "pb-6 border-b" : ""}
                    `}
                    >
                        <h4 className="text-[16px]">Document Information</h4>
                    </div>
                    <div className="px-6 pb-6 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-[12px] muted-text">Document ID</p>
                                <p className="font-medium text-[14px] title-text">{document?.doc_code}</p>
                            </div>
                                <div>
                                <p className="text-[12px] muted-text">Status</p>
                                <span className={`status-indicator ${statusClass}`}>
                                    {document?.doc_status_name}
                                </span>
                            </div>
                                <div>
                                <p className="text-[12px] muted-text">Date Issued</p>
                                <p className="font-medium text-[14px] title-text">{document?.doc_date}</p>
                            </div>
                            <div>
                                <p className="text-[12px] muted-text">Document Type</p>
                                <p className="font-medium text-[14px] title-text">{document?.doc_type_name}</p>
                            </div>
                        </div>
                        <div className="bg-gray-200 h-px w-full my-6"></div>
                        <div>
                            <p className="text-[12px] mb-2 muted-text">Description</p>
                            <p className="text-[14px] title-text">{document.doc_description}</p>
                        </div>
                        <div className="bg-gray-200 h-px w-full my-6"></div>
                        {document?.doc_meta?.prevSalary && document?.doc_meta?.newSalary && document?.doc_meta?.increase && (
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <p className="text-[12px] muted-text">Previous Salary</p>
                                    <p className="font-medium text-[14px] title-text">{document?.doc_meta?.prevSalary}</p>
                                </div>
                                <div>
                                    <p className="text-[12px] muted-text">New Salary</p>
                                    <p className="font-medium text-[14px] text-emerald-600 dark:text-emerald-500">{document?.doc_meta?.newSalary}</p>
                                </div>
                                <div>
                                    <p className="text-[12px] muted-text">Increase</p>
                                    <p className="inline-flex items-center justify-center rounded-lg px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700">{document?.doc_meta?.increase}</p>
                                </div>
                                <div>
                                    <p className="text-[12px] muted-text">Effective Date</p>
                                    <p className="font-medium text-[14px] title-text">{document?.doc_effective_date}</p>
                                </div>
                            </div>
                        )}

                        {document?.doc_meta?.previousRole && document?.doc_meta?.newRole && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[12px] muted-text">Previous Role</p>
                                    <p className="font-medium text-[14px] title-text">{document?.doc_meta?.previousRole}</p>
                                </div>
                                <div>
                                    <p className="text-[12px] muted-text">New Role</p>
                                    <p className="font-medium text-[14px] text-purple-600 dark:text-purple-500">{document?.doc_meta?.newRole}</p>
                                </div>
                                <div>
                                    <p className="text-[12px] muted-text">Effective Date</p>
                                    <p className="font-medium text-[14px] title-text">{document?.doc_effective_date}</p>
                                </div>
                            </div>
                        )}

                        {document?.doc_meta?.bonus && document?.doc_meta?.reason && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-[12px] muted-text">Bonus Amount</p>
                                    <p className="font-medium text-[16px] text-emerald-600 dark:text-emerald-500">{document?.doc_meta?.bonus}</p>
                                </div>
                                <div>
                                    <p className="text-[12px] muted-text">Reason</p>
                                    <p className="font-medium text-[14px] title-text">{document?.doc_meta?.reason}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-6 rounded-xl border border-slate-200 bg-slate-50">
                    <div className="pb-6 p-8">
                        <div className="flex flex-col items-center justify-center space-y-4 py-12">
                            <div className="p-4 rounded-full bg-slate-200">
                                <FileText className="w-12 h-12 text-slate-500" />
                            </div>
                            <div className="text-center space-y-2">
                                <p className="text-[14px] font-medium title-text">Document Preview</p>
                                <p className="text-[12px] max-w-md muted-text">
                                    Preview functionality would display the actual document content here. 
                                    Click download to get the full document.
                                </p>
                                <Button
                                    type="button"
                                    className="access-button btn-emerald h-8"
                                >
                                    <Download className="w-4 h-4 mr-2"/>
                                    <span className="text-sm font-medium">Download Full Document</span>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default DocumentInfo;
